import React, { useState } from 'react';
import './styles.css';
import { useTheme } from '@ohif/ui';

// Feature value can be a string, number, boolean, or null (if not available)
type FeatureValue = string | number | boolean | null;

// Feature interface for a specific plan
interface PlanFeature {
  name: string;
  value: FeatureValue;
}

// Plan interface
interface Plan {
  id: string;
  name: string;
  // monthlyPrice: number;
  // yearlyPrice: number;
  planDesc: string;
  features: PlanFeature[];
}

interface ToggleSwitchProps {
  isMonthly: boolean;
  onToggle: () => void;
}

interface PlanPreviewProps {
  formPlans: Array<{
    _id: string;
    name: string;
    monthly: {
      planPrice: string;
      users: number;
      cloudStorage: number;
      speechToText?: boolean;
      aiServices?: boolean;
      [key: string]: any;
    };
    yearly: {
      planPrice: string;
      users: number;
      cloudStorage: number;
      speechToText?: boolean;
      aiServices?: boolean;
      [key: string]: any;
    };
    [key: string]: any;
  }>;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ isMonthly, onToggle }) => {
  return (
    <div className="pricing-toggle">
      <span className={`toggle-option ${isMonthly ? 'active' : ''}`}>Monthly</span>
      <label className="toggle-switch-container">
        <input
          type="checkbox"
          checked={!isMonthly}
          onChange={onToggle}
          className="toggle-input"
          aria-label="Toggle between monthly and yearly pricing"
        />
        <span className="toggle-slider"></span>
      </label>
      <span className={`toggle-option ${!isMonthly ? 'active' : ''}`}>Yearly</span>
    </div>
  );
};

const CheckIcon: React.FC = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#0ea7a2"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="check-icon"
    >
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );
};

const CrossIcon: React.FC = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#e53e3e"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="cross-icon"
    >
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
};

const PlanPreview: React.FC<PlanPreviewProps> = ({ formPlans }) => {
  const isDarkMode = useTheme();
  const [isMonthly, setIsMonthly] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string>('');

  const formatPrice = (price: number): string => {
    return price === 0 ? 'Free' : `₹${price.toFixed(2)}`;
  };


  // Feature names in the order they'll be displayed
  const featureNames = [
    'Price',
    'Credits',
    'Users',
    'Cloud Storage',
    'Speech to Text',
    'AI Services',
    'Custom Features'
  ];

  // Convert formPlans to the Plan structure expected by the component
  const plans: Plan[] = formPlans.map(plan => ({
    id: plan?._id,
    name: plan?.name,
    planDesc: plan?.planDesc,
    // monthlyPrice: parseFloat(plan.monthly.planPrice) || 0,
    // yearlyPrice: parseFloat(plan.yearly.planPrice) || 0,
    features: [
      { name: 'Price', value: isMonthly ? `${formatPrice(parseFloat(plan?.monthly?.planPrice))}/month` : `${formatPrice(parseFloat(plan?.yearly?.planPrice))}/year` },
      { name: 'Credits', value: isMonthly ? plan?.monthly?.planCredits : plan?.yearly?.planCredits },
      { name: 'Users', value: isMonthly ? plan?.monthly?.users : plan?.yearly?.users },
      {
        name: 'Cloud Storage',
        value: isMonthly
          ? `${plan?.monthly?.cloudStorage || 0} GB`
          : `${plan?.yearly?.cloudStorage || 0} GB`
      },
      {
        name: 'Speech to Text',
        value: isMonthly
          ? plan?.monthly?.speechToText || false
          : plan?.yearly?.speechToText || false
      },
      {
        name: 'AI Services',
        value: isMonthly
          ? plan?.monthly?.aiServices || false
          : plan?.yearly?.aiServices || false
      },
      {
        name: 'Custom Features',
        value: isMonthly
          ? plan?.monthly?.customFeatures || []
          : plan?.yearly?.customFeatures || []
      }
    ]
  }));

  const togglePricingPeriod = () => {
    setIsMonthly(!isMonthly);
  };

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
  };

  // const formatPrice = (price: number): string => {
  //   return price === 0 ? 'Free' : `₹${price.toFixed(2)}`;
  // };

  return (
    <div className="plan-preview-cover" onClick={(e) => e.stopPropagation()}>
      <div className="toggle-container">
        <ToggleSwitch
          isMonthly={isMonthly}
          onToggle={togglePricingPeriod}
        />
      </div>

      <div className="plans-section">
        <div className="pricing-table-container">
          {/* Plans Header Row */}
          <div className="pricing-table-header">
            <div className="features-column-header">
              {/* <div className="feature-row">
                <div className="feature-name">Features</div>
              </div> */}
            </div>
            <div className="pricing-cards-container">
              {plans.map((plan) => (
                <div
                  className={`pricing-card ${selectedPlan === plan.id ? 'selected' : ''}`}
                  style={{background: isDarkMode?.isDarkMode ? "" : "#f8f9fa", borderRadius: "0"}}
                  key={plan.id}
                  onClick={() => handleSelectPlan(plan.id)}
                >
                  <div className="pricing-card-header" style={{ border: "none", background: "transparent", margin: "5px"}}>
                    <h4>{plan.name}</h4>
                    <div className="plan-price">
                      {/* {formatPrice(isMonthly ? plan.monthlyPrice : plan.yearlyPrice)} */}
                      {plan?.planDesc}
                      {/* <span className="billing-period">/{isMonthly ? 'month' : 'year'}</span> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Features and Plan Availability */}
          <div className="pricing-table-body">
            <div className="features-column">
              {featureNames.map((featureName, index) => (
                <div className="feature-row" key={index}>
                  <div className="feature-name" style={{color: isDarkMode?.isDarkMode ? '#fff' : '#4a5568'}}>{featureName}</div>
                </div>
              ))}
            </div>

            <div className="plan-features-container">
              {plans.map((plan) => (
                <div className="plan-features-column" key={plan.id}>
                  {plan.features.map((feature, index) => (
                    <div className="feature-indicator" key={`${plan.id}-${index}`}>
                      {feature.name === 'Custom Features' ? (
                        <div className="custom-features-list">
                          {Array.isArray(feature.value) ? (
                            feature.value.length > 0 ? (
                              <div className="custom-features-container">
                                <div className="custom-features-content">
                                  {feature.value.map((customFeature, idx) => (
                                    <div key={idx} className="custom-feature-item">
                                      <span className="feature-value">{customFeature}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <CrossIcon />
                            )
                          ) : (
                            <CrossIcon />
                          )}
                        </div>
                      ) : typeof feature.value === 'boolean' ? (
                        feature.value ? <CheckIcon /> : <CrossIcon />
                      ) : feature.value === null ? (
                        <CrossIcon />
                      ) : (
                        <span className="feature-value">{feature.value}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanPreview;
